import { useEffect, useState } from "react";

export type ThemePreference = "light" | "dark";

// Keep in sync with the inline script in index.html, which applies the saved
// theme before React loads so the page never flashes the wrong colors.
// Light is the default; dark only applies once chosen on this device.
const THEME_STORAGE_KEY = "imobi-theme";
const THEME_CHANGED_EVENT = "imobi:theme-changed";
const THEME_COLORS: Record<ThemePreference, string> = { light: "#0f766e", dark: "#0d121b" };

function readStoredTheme(): ThemePreference {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function getCurrentTheme(): ThemePreference {
  if (typeof document === "undefined") {
    return "light";
  }
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function applyTheme(theme: ThemePreference) {
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLORS[theme]);
}

/** Applies the theme and remembers it on this device only. */
export function setTheme(theme: ThemePreference) {
  applyTheme(theme);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage blocked (private mode): the theme still applies for this visit.
  }
  window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT));
}

export function useTheme(): [ThemePreference, (theme: ThemePreference) => void] {
  const [theme, setThemeState] = useState<ThemePreference>(getCurrentTheme);

  useEffect(() => {
    const sync = () => setThemeState(getCurrentTheme());
    // The choice was changed in another tab of this browser.
    function handleStorage(event: StorageEvent) {
      if (event.key === THEME_STORAGE_KEY) {
        applyTheme(readStoredTheme());
        sync();
      }
    }

    window.addEventListener(THEME_CHANGED_EVENT, sync);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, sync);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return [theme, setTheme];
}
